import { useState, type FormEvent } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/hooks/useAuth";

export default function AdminLoginPage() {
  const navigate = useNavigate();
  const { signInAsAdmin } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    if (!email.trim() || !password.trim()) {
      setError("Please enter your admin email and password.");
      return;
    }
    setSubmitting(true);
    try {
      await signInAsAdmin(email, password);
      navigate("/admin");
    } catch (err) {
      setError(err instanceof Error ? err.message : "Something went wrong. Try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const fillDemo = () => {
    setEmail("admin@purenest.com");
    setPassword("admin123");
    setError(null);
  };

  return (
    <div className="min-h-screen bg-foreground-950 text-background-50 flex flex-col relative overflow-hidden">
      {/* Decorative backdrop */}
      <div className="absolute inset-0 opacity-60 pointer-events-none">
        <div className="absolute -top-32 -left-24 w-[28rem] h-[28rem] rounded-full bg-primary-500/20 blur-3xl"></div>
        <div className="absolute -bottom-40 -right-32 w-[32rem] h-[32rem] rounded-full bg-accent-500/15 blur-3xl"></div>
        <div className="absolute top-1/3 left-1/2 w-72 h-72 rounded-full bg-secondary-500/15 blur-3xl"></div>
      </div>

      {/* Top bar */}
      <header className="relative z-10 px-6 md:px-12 py-6 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-2.5 group">
          <span className="w-9 h-9 flex items-center justify-center rounded-full bg-primary-500">
            <i className="ri-leaf-line text-lg text-background-50"></i>
          </span>
          <div className="leading-tight">
            <p className="font-heading text-base font-semibold tracking-tight">PureNest</p>
            <p className="text-[10px] uppercase tracking-[0.22em] text-background-50/55">
              Admin console
            </p>
          </div>
        </Link>
        <Link
          to="/login"
          className="inline-flex items-center gap-1.5 text-xs md:text-sm text-background-50/70 hover:text-background-50 whitespace-nowrap"
        >
          <i className="ri-user-line"></i>
          Customer sign in
        </Link>
      </header>

      <main className="relative z-10 flex-1 flex items-center justify-center px-6 py-8">
        <div className="w-full max-w-md">
          <div className="text-center mb-8">
            <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-background-50/10 text-background-50/85 text-[11px] tracking-[0.2em] uppercase font-medium ring-1 ring-background-50/15">
              <i className="ri-shield-keyhole-line text-accent-400"></i>
              Restricted area
            </span>
            <h1 className="font-heading text-3xl md:text-4xl font-medium leading-[1.1] mt-5">
              Sign in to the admin console.
            </h1>
            <p className="mt-3 text-background-50/65 text-sm">
              Manage bookings, cleaners, payouts and team settings — all in one place.
            </p>
          </div>

          <div className="bg-background-50/[0.04] backdrop-blur-sm border border-background-50/10 rounded-2xl p-6 md:p-7">
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label
                  htmlFor="admin-email"
                  className="block text-xs font-medium uppercase tracking-widest text-background-50/65 mb-2"
                >
                  Admin email
                </label>
                <div className="relative">
                  <i className="ri-mail-line absolute left-4 top-1/2 -translate-y-1/2 text-background-50/55"></i>
                  <input
                    id="admin-email"
                    name="email"
                    type="email"
                    autoComplete="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="you@purenest.com"
                    className="w-full pl-11 pr-4 py-3 rounded-lg bg-background-50/5 border border-background-50/15 text-sm text-background-50 placeholder:text-background-50/35 focus:outline-none focus:ring-2 focus:ring-primary-400/60 focus:border-primary-400/60"
                  />
                </div>
              </div>

              <div>
                <div className="flex items-center justify-between mb-2">
                  <label
                    htmlFor="admin-password"
                    className="text-xs font-medium uppercase tracking-widest text-background-50/65"
                  >
                    Password
                  </label>
                  <a
                    href="#"
                    className="text-xs text-primary-300 hover:text-primary-200 font-medium"
                  >
                    Need help?
                  </a>
                </div>
                <div className="relative">
                  <i className="ri-lock-2-line absolute left-4 top-1/2 -translate-y-1/2 text-background-50/55"></i>
                  <input
                    id="admin-password"
                    name="password"
                    type={showPw ? "text" : "password"}
                    autoComplete="current-password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="Enter your password"
                    className="w-full pl-11 pr-12 py-3 rounded-lg bg-background-50/5 border border-background-50/15 text-sm text-background-50 placeholder:text-background-50/35 focus:outline-none focus:ring-2 focus:ring-primary-400/60 focus:border-primary-400/60"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPw((v) => !v)}
                    className="absolute right-4 top-1/2 -translate-y-1/2 text-background-50/55 hover:text-background-50 cursor-pointer"
                    aria-label={showPw ? "Hide password" : "Show password"}
                  >
                    <i className={showPw ? "ri-eye-off-line" : "ri-eye-line"}></i>
                  </button>
                </div>
              </div>

              {error && (
                <div className="flex items-start gap-2 px-4 py-3 rounded-lg bg-accent-500/15 border border-accent-500/30 text-accent-200 text-sm">
                  <i className="ri-error-warning-line mt-0.5"></i>
                  <span>{error}</span>
                </div>
              )}

              <button
                type="submit"
                disabled={submitting}
                className="group w-full flex items-center justify-center gap-2 bg-primary-500 hover:bg-primary-600 text-background-50 px-6 py-3.5 rounded-lg text-sm font-medium whitespace-nowrap transition-colors disabled:opacity-60 disabled:cursor-not-allowed cursor-pointer"
              >
                {submitting ? (
                  <>
                    <i className="ri-loader-4-line animate-spin"></i>
                    Verifying access…
                  </>
                ) : (
                  <>
                    <i className="ri-shield-check-line"></i>
                    Enter admin console
                  </>
                )}
              </button>
            </form>

            <div className="mt-6 pt-5 border-t border-background-50/10">
              <div className="flex items-start gap-3">
                <span className="w-9 h-9 flex items-center justify-center rounded-lg bg-background-50/10 flex-shrink-0">
                  <i className="ri-magic-line text-accent-300"></i>
                </span>
                <div className="flex-1 min-w-0">
                  <p className="text-xs font-medium text-background-50/85">
                    Want to peek inside?
                  </p>
                  <p className="text-[11px] text-background-50/55 mt-0.5">
                    Use the built-in demo admin account to take a look around.
                  </p>
                </div>
                <button
                  type="button"
                  onClick={fillDemo}
                  className="text-xs font-medium text-primary-300 hover:text-primary-200 whitespace-nowrap cursor-pointer"
                >
                  Auto-fill
                </button>
              </div>
            </div>
          </div>

          <div className="mt-6 flex items-center justify-center gap-2 text-[11px] text-background-50/45">
            <i className="ri-lock-line"></i>
            <span>All sessions are encrypted · SOC 2 Type II</span>
          </div>
        </div>
      </main>

      <footer className="relative z-10 px-6 md:px-12 py-5 flex flex-wrap items-center justify-between gap-3 text-[11px] text-background-50/50">
        <span>© {new Date().getFullYear()} PureNest. Internal use only.</span>
        <div className="flex items-center gap-4">
          <a href="#" className="hover:text-background-50">Status</a>
          <a href="#" className="hover:text-background-50">Security</a>
          <a href="#" className="hover:text-background-50">Contact IT</a>
        </div>
      </footer>
    </div>
  );
}